/**
 * Auto-layout for the workflow canvas — a deterministic LAYERED layout
 * (owner ask after the Odino draft-first arc: collision-nudging at commit
 * keeps nodes apart, but repeated model edits leave the graph sprawled; this
 * reorders it in ONE click, operator-triggered — never applied silently).
 *
 * Columns = longest-path depth from the roots (left → right, the execution
 * reading direction). Rows are GLOBAL LANES inherited from the predecessors —
 * NOT per-column dense indexes: dense packing collapses every single-node
 * column back to row 0, so a chain that starts one row down (a loop body)
 * jumps straight back onto the main line and the edges zig-zag (2nd live
 * "mmmmm" screenshot). A node wants its predecessors' lane; a LOOP BODY node
 * (on the loop's cycle) wants one lane BELOW its loop — the n8n look: main
 * line + Done branch straight through, the body a lane under it, the
 * back-edge curving home. Conflicts in a column bump downward.
 *
 * The ONE legal cycle — a `loop` node's back-edge — is broken by stripping
 * ONLY the genuine back-edge: an edge into a loop node whose source sits
 * inside that loop's own body (reachable FROM the loop). Stripping EVERY edge
 * into a loop (the acyclicity-check shortcut `validateGraph` uses) is WRONG
 * here: it also cuts the FORWARD edge feeding the loop, re-rooting it at
 * depth 0 on top of the trigger and scrambling the whole layout (the owner's
 * first live use came out exactly like that).
 *
 * Pure (id/edge lists in, positions out) so it is unit-testable without
 * ReactFlow; the editor applies the map to its live nodes.
 */

export interface LayoutEdge {
  source: string;
  target: string;
}

/** Horizontal gap between depth columns / vertical gap between rows. */
export const LAYOUT_X_GAP = 300;
export const LAYOUT_Y_GAP = 140;

export function autoLayoutPositions(
  nodeIds: string[],
  edges: LayoutEdge[],
  loopIds: ReadonlySet<string> = new Set(),
): Map<string, { x: number; y: number }> {
  const ids = new Set(nodeIds);
  const valid = edges.filter((e) => ids.has(e.source) && ids.has(e.target));

  const out = new Map<string, string[]>();
  for (const e of valid) {
    const list = out.get(e.source) ?? [];
    list.push(e.target);
    out.set(e.source, list);
  }

  // Reachability over the FULL edge set (cycles are fine — the visited set
  // bounds the walk), memoised per start node.
  const reachMemo = new Map<string, Set<string>>();
  const reachableFrom = (start: string): Set<string> => {
    const memo = reachMemo.get(start);
    if (memo) return memo;
    const seen = new Set<string>();
    const stack = [start];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const nxt of out.get(cur) ?? []) {
        if (!seen.has(nxt)) {
          seen.add(nxt);
          stack.push(nxt);
        }
      }
    }
    reachMemo.set(start, seen);
    return seen;
  };

  // Strip ONLY the loop's genuine back-edge (see the module docstring).
  const acyclic = valid.filter(
    (e) => !(loopIds.has(e.target) && reachableFrom(e.target).has(e.source)),
  );

  const preds = new Map<string, string[]>();
  for (const e of acyclic) {
    const list = preds.get(e.target) ?? [];
    list.push(e.source);
    preds.set(e.target, list);
  }

  // Longest-path depth, memoised; a defensive visiting-guard turns any residual
  // cycle (corrupt data) into depth 0 instead of an infinite recursion.
  const depth = new Map<string, number>();
  const visiting = new Set<string>();
  const depthOf = (id: string): number => {
    const memo = depth.get(id);
    if (memo !== undefined) return memo;
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const ps = preds.get(id) ?? [];
    const d = ps.length ? Math.max(...ps.map(depthOf)) + 1 : 0;
    visiting.delete(id);
    depth.set(id, d);
    return d;
  };
  for (const id of nodeIds) depthOf(id);

  // A loop's BODY = the nodes on its cycle (reachable from the loop AND able
  // to reach it back). The Done branch is reachable but can't reach back, so
  // it stays on the loop's own lane.
  const bodyOf = new Map<string, Set<string>>();
  for (const l of loopIds) {
    if (!ids.has(l)) continue;
    const body = new Set<string>();
    for (const n of reachableFrom(l)) {
      if (n !== l && reachableFrom(n).has(l)) body.add(n);
    }
    bodyOf.set(l, body);
  }

  const inputIdx = new Map(nodeIds.map((id, i) => [id, i] as const));
  const byCol = new Map<number, string[]>();
  for (const id of nodeIds) {
    const d = depth.get(id) ?? 0;
    const col = byCol.get(d) ?? [];
    col.push(id);
    byCol.set(d, col);
  }

  // Lane assignment, columns left → right (a pred is always in an EARLIER
  // column under longest-path depth, so its lane is already set): desired =
  // rounded average of predecessor lanes, +1 when the pred is a loop and this
  // node is in its body. Same-column conflicts resolve by desired (then input
  // order), bumping down to the next free lane.
  const lane = new Map<string, number>();
  const pos = new Map<string, { x: number; y: number }>();
  for (const d of [...byCol.keys()].sort((a, b) => a - b)) {
    const scored = byCol.get(d)!.map((id) => {
      const wants = (preds.get(id) ?? [])
        .map((p) => {
          const pl = lane.get(p);
          if (pl === undefined) return undefined;
          return pl + (loopIds.has(p) && bodyOf.get(p)?.has(id) ? 1 : 0);
        })
        .filter((l): l is number => l !== undefined);
      return {
        id,
        desired: wants.length ? Math.round(wants.reduce((a, b) => a + b, 0) / wants.length) : 0,
      };
    });
    scored.sort((a, b) => a.desired - b.desired || inputIdx.get(a.id)! - inputIdx.get(b.id)!);
    const used = new Set<number>();
    for (const { id, desired } of scored) {
      let row = desired;
      while (used.has(row)) row += 1;
      used.add(row);
      lane.set(id, row);
      pos.set(id, { x: d * LAYOUT_X_GAP, y: row * LAYOUT_Y_GAP });
    }
  }
  return pos;
}
